import type {
  Favorite,
  FavoriteApiResponse,
  FavoriteStatus,
  FavoriteTarget,
} from "../types/favorite";
import useUserStore from "../store/userStore";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FAVORITES_URL = `${BASE_URL}/api/favorites/`;

const getAuthHeaders = (): HeadersInit => {
  const { token } = useUserStore.getState();

  if (!token) return {};

  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
  };
};

type FavoriteApiItem = Omit<Favorite, "model_type" | "object_id"> & {
  model_type?: FavoriteTarget;
  item_type?: FavoriteTarget;
  object_id: string | number;
};

const normalizeFavorite = (
  favorite: FavoriteApiItem,
  fallbackTarget: FavoriteTarget = "place"
): Favorite => {
  const target = favorite.model_type ?? favorite.item_type ?? fallbackTarget;

  return {
    ...favorite,
    model_type: target,
    item_type: favorite.item_type ?? target,
    object_id: String(favorite.object_id),
  };
};

const getErrorMessage = async (response: Response): Promise<string> => {
  const body = await response.json().catch(() => null);

  return (
    body?.error?.message ??
    body?.message ??
    body?.detail ??
    `Favorites request failed with status ${response.status}`
  );
};

const throwIfNotOk = async (response: Response): Promise<void> => {
  if (!response.ok) {
    throw new Error(await getErrorMessage(response));
  }
};

export const getFavorites = async (): Promise<Favorite[]> => {
  const response = await fetch(FAVORITES_URL, {
    headers: getAuthHeaders(),
    credentials: "include",
  });

  await throwIfNotOk(response);

  const body: FavoriteApiResponse<FavoriteApiItem[]> = await response.json();
  return (body.data ?? []).map((favorite) => normalizeFavorite(favorite));
};

export const getFavoriteStatus = async (
  modelType: FavoriteTarget,
  objectId: string
): Promise<FavoriteStatus> => {
  const params = new URLSearchParams({
    model_type: modelType,
    object_id: objectId,
  });
  const response = await fetch(
    `${FAVORITES_URL}status/?${params.toString()}`,
    {
      headers: getAuthHeaders(),
      credentials: "include",
    }
  );

  await throwIfNotOk(response);

  const body: FavoriteApiResponse<FavoriteStatus> = await response.json();
  return body.data;
};

export const addFavorite = async (
  modelType: FavoriteTarget,
  objectId: string
): Promise<Favorite> => {
  const response = await fetch(FAVORITES_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
    body: JSON.stringify({
      model_type: modelType,
      object_id: objectId,
    }),
  });

  await throwIfNotOk(response);

  const body: FavoriteApiResponse<FavoriteApiItem> = await response.json();
  return normalizeFavorite(body.data, modelType);
};

export const removeFavorite = async (
  favoriteId: number
): Promise<void> => {
  const response = await fetch(
    `${FAVORITES_URL}${favoriteId}/`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
      credentials: "include",
    }
  );

  await throwIfNotOk(response);
};
